import type { MobilistOverrideNote } from '../types/vehicle';

/**
 * How an override decision reads in the UI.
 *
 * Both the badge tooltip and the history timeline show the same three things — what was decided,
 * by whom, and when — so the formatting lives here rather than being written twice.
 */

/** Dutch locale: the portal's users are VWPFS staff and the backoffice reads dates that way. */
const DATE_FORMAT = new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });

export function formatOverrideDate(at: string): string {
  const date = new Date(at);

  // An unparseable timestamp is not worth breaking a row over; show what we were given.
  return Number.isNaN(date.getTime()) ? at : DATE_FORMAT.format(date);
}

/**
 * Tooltip text for a status badge.
 *
 * Names the actor even with no reason given — "who and when" is still an answer, and it tells the
 * reader the History action has something to show.
 */
export function formatOverrideNote(note: MobilistOverrideNote | null | undefined): string | undefined {
  if (!note) {
    return undefined;
  }

  const attribution = `${note.actor} · ${formatOverrideDate(note.at)}`;
  const reason = note.reason?.trim();

  return reason ? `${reason} — ${attribution}` : `No reason given — ${attribution}`;
}

/** The later of two notes, or whichever exists. Used when only "what happened last" matters. */
function newestNote(
  first: MobilistOverrideNote | null | undefined,
  second: MobilistOverrideNote | null | undefined
): MobilistOverrideNote | undefined {
  if (!first) return second ?? undefined;
  if (!second) return first;

  return first.at >= second.at ? first : second;
}

/**
 * Tooltip for a vehicle that carries no override right now.
 *
 * Without this, clearing an override takes its reason off the row with it: the badge it hung from
 * is gone, so "why is this available again?" could only be answered by opening History. Phrased as
 * "Last change" so it never reads as a live override.
 */
export function formatLastChangeNote(
  reserveNote: MobilistOverrideNote | null | undefined,
  adBlockNote: MobilistOverrideNote | null | undefined
): string | undefined {
  const note = newestNote(reserveNote, adBlockNote);

  if (!note) {
    return undefined;
  }

  const attribution = `${note.actor} · ${formatOverrideDate(note.at)}`;
  const reason = note.reason?.trim();

  return reason ? `Last change: ${reason} — ${attribution}` : `Last change by ${attribution}, no reason given`;
}
